import * as React from 'react';
import { Fragment, useState, useEffect } from 'react';

/** Material UI Packages. */
import { Toolbar, List, Tooltip, ListItemButton, Typography } from '@mui/material';

/** Material UI Icons. */
import IconButton from '@mui/material/IconButton';
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import LogoutIcon from '@mui/icons-material/Logout';

/** Client-side routing Library.  */
import { Link, useLocation } from 'react-router-dom';

/** Auth0 authenticator Packages. */
import { useAuth0 } from '@auth0/auth0-react';

/** Static Assets. */
import insight_logo_horizontal from '../statics/images/insight_logo_horizontal.png';

/** Internal Components. */
import { customized_style } from './Styles';
import SubMenu from './SubMenu';
import useUserRole from '../utils/useUserRole';

export default function Menubar() {
    /** Auth0 authenticator. */
    const { logout } = useAuth0();

    /** Get the current location. */
    const location = useLocation();
    const userRole = useUserRole();

    /** State for endpoint value of selected Menu list.  */
    const [selectedItem, setSelectedItem] = useState(null);
    /** State to manage endpoint when click the logo image. */
    const [homeValue, setHomeValue] = useState('');
    /** State to manage the currently opened menu (for submenu). */
    const [anchorEl, setAnchorEl] = useState(null);
    /** State to store the type of menu that is currently open (operation, reports, management). */
    const [menuType, setMenuType] = useState(null);

    /** Boolean to track if submenu is open based on the `anchorEl` state. */
    const open = Boolean(anchorEl);

    /** Handle to Set the anchor element and the type of menu being opened. */
    const handleClick = (event, type) => {
        setAnchorEl(event.currentTarget);
        setMenuType(type);
    };

    /** Resets the anchor element and clears the menu type state. */
    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuType(null);
    };

    /** Handler to update selected Value.  */
    const handleItemClick = (item) => {
        setSelectedItem(item);
    };

    /** Handle for changing homeValue and selectedItem when you click a home image. */
    const handleHomeClick = () => {
        setHomeValue('/dashboard');
        setSelectedItem('smartsystem');
    };

    /** Initiallize menu selecttion based on userRole. */
    useEffect(() => {
        const location_path = location.pathname.slice(1);
        let selectedKey = null;
        if (location_path === 'myprofile') {
            selectedKey = 'myprofile';
        } else {
            // Iterate through linkData to check its values.
            for (const [key, values] of Object.entries(linkData)) {
                if (values.includes(location_path)) {
                    selectedKey = key;
                    break;
                }
            }
        }

        // Update selectedItem if a value is selected; otherwise, set it to the default 'dashboard'.
        setSelectedItem(selectedKey || 'dashboard');
    }, [location]);

    /** Menu data object containing items for each menu section. */
    const menuData = {
        smartsystem: [
            { label: 'Account', legacy: false },
            { label: 'Account', legacy: true },
            { label: 'Addons', legacy: false },
        ],
    };

    const linkData = {
        smartsystem: ['accountsetting', 'account', 'addons'],
    };

    const getFilteredMenuData = (menuType) => {
        if (!userRole.some((role) => ['Administrator', 'Executive'].includes(role))) {
            return [];
        }

        const isAdministrator = userRole.includes('Administrator');
        const isExecutive = userRole.includes('Executive');

        if (menuType === 'smartsystem') {
            if (isAdministrator) {
                // Administrator는 모든 메뉴 접근 가능
                return menuData.smartsystem;
            } else if (isExecutive) {
                // Executive는 addons 제외
                return menuData.smartsystem.filter((item) => item.label.toLowerCase() !== 'addons');
            }
        }
        return [];
    };

    const getFilteredLinkData = (menuType) => {
        if (!userRole.some((role) => ['Administrator', 'Executive'].includes(role))) {
            return [];
        }

        const isAdministrator = userRole.includes('Administrator');
        const isExecutive = userRole.includes('Executive');

        if (menuType === 'smartsystem') {
            if (isAdministrator) {
                // Administrator는 모든 링크 접근 가능
                return linkData.smartsystem;
            } else if (isExecutive) {
                // Executive는 addons 제외
                return linkData.smartsystem.filter((link) => link !== 'addons');
            }
        }
        return [];
    };

    return (
        <Fragment>
            <Toolbar
                sx={{
                    bgcolor: 'white',
                    color: '#00657e',
                    minHeight: 48,
                }}
                variant="dense"
            >
                <Link to={homeValue} style={{ cursor: 'pointer' }} onClick={handleHomeClick}>
                    <img
                        src={insight_logo_horizontal}
                        alt="Insight Logo"
                        style={{
                            height: '48px',
                            marginRight: 'auto',
                            marginLeft: 0,
                        }}
                    />
                </Link>
                <Typography sx={{ flexGrow: 1 }}></Typography>
                <List sx={{ display: 'flex', flexDirection: 'row', padding: 0 }}>
                    <Fragment>
                        {userRole.some((role) => ['Administrator', 'Executive'].includes(role)) && (
                            <>
                                <ListItemButton
                                    selected={selectedItem === 'dashboard'}
                                    sx={customized_style.itemMenu}
                                    component={Link}
                                    to="/dashboard"
                                    onClick={() => handleItemClick('dashboard')}
                                >
                                    DASHBOARD
                                </ListItemButton>
                                <ListItemButton
                                    onClick={(event) => handleClick(event, 'smartsystem')}
                                    selected={selectedItem === 'smartsystem'}
                                    sx={customized_style.itemMenu}
                                >
                                    SMART SYSTEM
                                </ListItemButton>
                                <SubMenu
                                    anchorEl={anchorEl}
                                    open={open && menuType === 'smartsystem'}
                                    handleClose={handleMenuClose}
                                    // menuItems={menuData.smartsystem}
                                    // linkItems={linkData.smartsystem}
                                    menuItems={getFilteredMenuData('smartsystem')}
                                    linkItems={getFilteredLinkData('smartsystem')}
                                    handleItemClick={handleItemClick}
                                    menuType={menuType}
                                    name={'smartsystem'}
                                />
                            </>
                        )}
                    </Fragment>
                </List>
                <Tooltip title="My Profile">
                    <IconButton color="inherit" component={Link} to="/myprofile">
                        <InsertEmoticonIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="LogOut">
                    <IconButton
                        color="inherit"
                        onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                    >
                        <LogoutIcon />
                    </IconButton>
                </Tooltip>
            </Toolbar>
        </Fragment>
    );
}
